import { Grid24Px, R5Robot24Px, Workstation24Px } from "@locaisolutions/icons";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { basicColours, mainColours } from "@qubit/autoparts";
import { BouncingDotsLoader } from "@qubit/autoparts/src/robotLoader/BouncingDotsLoader";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BinAnimationCategory } from "./BinState";
import { BinsAbove } from "./BinsAbove";

const bouncingDotsStyle = {
  backgroundColor: "white",
  width: ".35em",
  height: ".35em",
  margin: ".2em"
};

const activeColor = basicColours.yellow[300];
const otherPortColor = mainColours.label[5];

const getIconProps = (isActive: boolean) => ({
  fill: isActive ? activeColor : "white",
  height: "40px",
  width: "40px"
});

const getTextProps = (isActive: boolean) => ({
  color: isActive ? activeColor : "white",
  fontSize: "1rem",
  fontWeight: 500,
  lineHeight: "1.2",
  textAlign: "center" as const
});

export const BinAnimationOverlay = ({
  animationCategory,
  parentWidth,
  binsAbove
}: {
  animationCategory: BinAnimationCategory;
  parentWidth?: number;
  binsAbove: number | undefined;
}) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const showOneIcon = parentWidth && parentWidth < 270;

  const inGrid = animationCategory === "In Grid";
  const onRobot = animationCategory === "On Robot";
  const inPort = animationCategory === "In Port";
  const otherPort = animationCategory === "Other Port";

  const SingleIconAnimation = useMemo(() => {
    switch (animationCategory) {
      case "In Grid":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <Grid24Px {...getIconProps(inGrid)} />
              <Typography {...getTextProps(inGrid)}>{t("in grid")}</Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
      case "On Robot":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <R5Robot24Px {...getIconProps(onRobot)} />
              <Typography {...getTextProps(onRobot)}>
                {t("on robot")}
              </Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
      case "In Port":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <Workstation24Px {...getIconProps(inPort)} />
              <Typography {...getTextProps(inPort)}>{t("in port")}</Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
      case "Other Port":
        return (
          <>
            <Stack alignItems="center" gap={0.5}>
              <Workstation24Px
                {...getIconProps(otherPort)}
                fill={otherPortColor}
              />
              <Typography {...getTextProps(otherPort)} color={otherPortColor}>
                {t("at another port")}
              </Typography>
            </Stack>
            <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading />
          </>
        );
    }
  }, [animationCategory, inGrid, onRobot, inPort, otherPort, t]);

  const MultiIconAnimation = (
    <>
      {otherPort ? (
        <Stack alignItems="center" gap={0.5} mt="14px">
          <Workstation24Px {...getIconProps(otherPort)} fill={otherPortColor} />
          <Typography {...getTextProps(otherPort)} color={otherPortColor}>
            {t("at another port")}
          </Typography>
        </Stack>
      ) : (
        <Stack alignItems="center" gap={0.5} position="relative">
          <Grid24Px {...getIconProps(inGrid)} />
          <Typography {...getTextProps(inGrid)}>{t("in grid")}</Typography>
          {binsAbove ? <BinsAbove binsAbove={binsAbove} /> : null}
        </Stack>
      )}

      <BouncingDotsLoader
        dotStyle={bouncingDotsStyle}
        isLoading={inGrid || otherPort}
      />

      <Stack alignItems="center" gap={0.5}>
        <R5Robot24Px {...getIconProps(onRobot)} />
        <Typography {...getTextProps(onRobot)}>{t("on robot")}</Typography>
      </Stack>

      <BouncingDotsLoader dotStyle={bouncingDotsStyle} isLoading={onRobot} />

      <Stack alignItems="center" gap={0.5}>
        <Workstation24Px {...getIconProps(inPort)} />
        <Typography {...getTextProps(inPort)}>{t("in port")}</Typography>
      </Stack>
    </>
  );

  return showOneIcon ? (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      flexGrow={1}
      gap="8px"
      bgcolor={palette.darkGray.main}
    >
      {SingleIconAnimation}
    </Stack>
  ) : (
    <Box
      display="grid"
      gridTemplateColumns="2fr 1fr 2fr 1fr 2fr"
      alignItems="center"
      flexGrow={1}
      p={2}
      bgcolor={palette.darkGray.main}
    >
      {MultiIconAnimation}
    </Box>
  );
};
